/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Storage } from '../../core/storage/Storage';
import { GlobalCatch } from '../../core/error/GlobalCatch';

interface GlobalCatchProps {
    name: string;
    onCatch: Function;
    retryLimit?: number | undefined;
}

const useGlobalCatch = ({ name, onCatch, retryLimit }: GlobalCatchProps) => {
    const globalCatch = new GlobalCatch(name);
    const sessionRefKey = `_${name}_RETRY_COUNT`;
    const catchCount =
        Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(sessionRefKey) || 0;

    const actualRetryLimit = retryLimit && retryLimit > 0 ? retryLimit : 1;
    const isRetryable = catchCount < actualRetryLimit;

    const processError = (error) => {
        if (!isRetryable) return;

        globalCatch.parse(error);
        globalCatch.save();

        if (actualRetryLimit > 1) {
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                sessionRefKey,
                catchCount + 1,
            );
        }

        onCatch();
    };

    const isRetriesDone = (): boolean => {
        return !isRetryable;
    };

    useEffect(() => {
        window.addEventListener('error', (ev: ErrorEvent) => {
            ev.preventDefault();
            processError(ev);
        });

        if (!isRetryable) {
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).removeItem(sessionRefKey);
        }

        return () => {
            window.removeEventListener('error', () => true);
        };
    }, []);

    return {
        current: globalCatch.getLatestCatch(),
        catches: globalCatch.getCatches,
        clear: globalCatch.clear,
        isRetriesDone,
    };
};

useGlobalCatch.propTypes = {
    name: PropTypes.string.isRequired,
    onCatch: PropTypes.func.isRequired,
    retryLimit: PropTypes.number,
};

export default useGlobalCatch;
